import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import {
  CompanyModel,
  CompanyDetailModel,
  CoefficientsConfig,
} from '@falcon/app/shared/models/company.model';
import { PageableResponsePayloadModel } from '@falcon/app/shared/models/pageable-response-payload.model';
import { MandateModel } from '@falcon/app/shared/models/mandate.model';
import { InvoiceModel } from '@falcon/app/shared/models/invoice.model';
import { CompanyAccount } from '@falcon/app/shared/models/company-account.model';
import { environment } from '@falcon/env';
import { BaseApi } from '../models/base-api';
import { CompanyUserRequestPayload } from '../models/company-user-request-payload.model';
import { CompaniesRequestParamsModel } from './company-requests-params.model';

@Injectable({
  providedIn: 'root',
})
export class CompanyService extends BaseApi {
  private http = inject(HttpClient);

  private readonly COMPANIES_API_URL = `${environment.apiURL}/companies`;
  private readonly COMPANY_CUSTOMERS_API_URL = `${environment.apiURL}/customers`;

  getCompany(id: string): Observable<CompanyDetailModel> {
    return this.http.get<CompanyDetailModel>(`${this.COMPANIES_API_URL}/${id}`);
  }

  getCompanies(
    params: CompaniesRequestParamsModel
  ): Observable<PageableResponsePayloadModel<CompanyModel[]>> {
    return this.http.get<PageableResponsePayloadModel<CompanyModel[]>>(this.COMPANIES_API_URL, {
      params: this.mapParamsToString(params),
    });
  }

  updateCompany(uuid: string, body: CompanyDetailModel): Observable<CompanyDetailModel> {
    return this.http.put<CompanyDetailModel>(
      `${this.COMPANIES_API_URL}/${uuid}`,
      this.mapBodyEmptyStringToNull(body)
    );
  }

  getCompanyCustomers(params: { companyId: string }): Observable<CompanyAccount[]> {
    return this.http.get<CompanyAccount[]>(`${this.COMPANY_CUSTOMERS_API_URL}`, {
      params: this.mapParamsToString(params),
    });
  }

  addCompanyCustomer(reqData: CompanyUserRequestPayload): Observable<void> {
    return this.http.post<void>(this.COMPANY_CUSTOMERS_API_URL, reqData);
  }

  removeCompanyCustomer(customerId: string): Observable<void> {
    return this.http.delete<void>(`${this.COMPANY_CUSTOMERS_API_URL}/${customerId}`);
  }

  getMandate(uuid: string): Observable<MandateModel> {
    return this.http.get<MandateModel>(`${this.COMPANIES_API_URL}/${uuid}/mandate`);
  }

  addMandate(uuid: string): Observable<void> {
    return this.http.post<void>(`${this.COMPANIES_API_URL}/${uuid}/mandate`, { companyId: uuid });
  }

  updateMandate(uuid: string, body: MandateModel): Observable<MandateModel> {
    return this.http.put<MandateModel>(
      `${this.COMPANIES_API_URL}/${uuid}/mandate`,
      this.mapBodyEmptyStringToNull(body)
    );
  }

  getCoefficientsConfig(): Observable<CoefficientsConfig> {
    return this.http.get<CoefficientsConfig>(`${this.COMPANIES_API_URL}/coefficients`);
  }

  updateInvoice(uuid: string, invoice: InvoiceModel): Observable<InvoiceModel[]> {
    return this.http
      .put<
        PageableResponsePayloadModel<InvoiceModel[]>
      >(`${this.COMPANIES_API_URL}/${uuid}/invoices`, this.mapBodyEmptyStringToNull(invoice))
      .pipe(map(res => res.content));
  }

  sendCompanyDataToHubSpot(uuid: string): Observable<void> {
    return this.http.post<void>(`${this.COMPANIES_API_URL}/${uuid}/tag`, {});
  }
}
